import {
    Badge,
    Banner,
    BlockStack,
    Button,
    Checkbox,
    DataTable,
    InlineGrid,
    InlineStack,
    Spinner,
    Text,
    TextField,
} from '@shopify/polaris';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SectionBody, SectionToggle, useSectionOpen } from './CollapsibleSection';
import apiClient from '../utils/api';

/**
 * The brand's monthly storage fee: what it pays 2W per unit held at 2W's warehouse location(s).
 *
 * On the 1st of each month the units on hand at the brand's 2W shipping locations (ticked in
 * Shipping Configuration above) that have been there longer than the free period are counted,
 * and the count is charged at the rate below on the invoice of the month just ended.
 *
 * The settings are a HISTORY: each save is in force from an invoice month, so changing the rate
 * never alters a month already billed. A month whose invoice is approved, sent or paid - or any
 * month before it - cannot be given new settings; the server refuses it, and this card offers the
 * first open month instead.
 *
 * Storage needs shipping: while the brand's shipping is disabled nothing is read, counted or
 * charged, and every invoice that can still change has no storage fee. The card says so and
 * locks its controls; the settings are kept for when shipping is enabled again.
 *
 * Each month's count is listed with its invoice. While that invoice is a draft or not made yet,
 * the units charged can be overridden with a note, and the month just ended can be counted
 * again from stock as it is now; either rebuilds a draft invoice.
 */

const pad = (n) => String(n).padStart(2, '0');

/** "2026-11" for a Date, in UTC. */
export const monthOf = (date) => `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}`;

/** The month after "YYYY-MM". */
export const nextMonth = (month) => {
    const [y, m] = month.split('-').map(Number);
    return m === 12 ? `${y + 1}-01` : `${y}-${pad(m + 1)}`;
};

/**
 * The month a new setting takes effect from by default: this month, or the month after the last
 * one already billed when that is later - the earliest month the server will accept.
 */
export const firstOpenMonth = (lockedThrough, now = new Date()) => {
    const thisMonth = monthOf(now);
    if (!lockedThrough) return thisMonth;
    const afterLocked = nextMonth(lockedThrough);
    return afterLocked > thisMonth ? afterLocked : thisMonth;
};

/** "$0.10" - at least two decimals, up to four. */
export const formatRate = (rate) => {
    const n = Number(rate);
    if (!Number.isFinite(n)) return '—';
    const four = n.toFixed(4).replace(/0{1,2}$/, '');
    return `$${four}`;
};

/** "6 months" / "1 month" / "None - charged from day one". */
export const formatFreeMonths = (months) => {
    if (months === null || months === undefined || months === '') return '—';
    const n = Number(months);
    if (!Number.isFinite(n)) return '—';
    if (n === 0) return 'None — charged from day one';
    return `${n} month${n === 1 ? '' : 's'}`;
};

/** "Nov 2026" for "2026-11". */
export const monthLabel = (month) => {
    if (!month) return '—';
    const [y, m] = month.split('-').map(Number);
    const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return names[m - 1] ? `${names[m - 1]} ${y}` : month;
};

/** One sentence: what this brand is charged for storage now. */
export const describeStorage = (settings) => {
    const current = settings?.current;
    if (!current || !current.enabled) {
        return settings?.startMonth
            ? 'Storage fees are off for this brand now.'
            : 'This brand is not charged for storage.';
    }
    const free = Number(current.freeMonths) === 0
        ? 'every unit on hand'
        : `units held longer than ${formatFreeMonths(current.freeMonths)}`;
    return `Charged ${formatRate(current.ratePerUnit)} per unit per month for ${free} at 2W's shipping `
        + `locations, since the ${monthLabel(settings.startMonth)} invoice.`;
};

/**
 * The message after a save: the month it applies from, and any later months whose settings it
 * replaced, since a save applies from its month onward.
 */
export const savedMessage = (response) => {
    const from = `Saved: in force from the ${monthLabel(response?.setting?.effectiveMonth)} invoice onward.`;
    const replaced = (response?.replaced || []).map((r) => monthLabel(r.effectiveMonth));
    return replaced.length
        ? `${from} It replaces the settings saved from ${replaced.join(', ')}.`
        : from;
};

/** Help for the month field, which shows the month the settings in force start from. */
export const effectiveMonthHelp = (settings) => {
    const earlier = settings?.lockedThrough
        ? `Months up to ${monthLabel(settings.lockedThrough)} are already billed and keep their settings.`
        : 'Earlier months keep the settings they were billed with.';
    return `A change applies to this month and every month after it. ${earlier}`;
};

/**
 * The form, filled from the setting in force, or from the defaults for a brand with none. The
 * month is the one the settings in force start from, as saved; a brand with none starts at the
 * first month that can still be changed.
 */
export const formFrom = (settings, now = new Date()) => {
    const current = settings?.current;
    const defaults = settings?.defaults || { freeMonths: 6, ratePerUnit: 0.1 };
    return {
        enabled: current ? current.enabled : false,
        freeMonths: String(current ? current.freeMonths : defaults.freeMonths),
        ratePerUnit: String(current ? current.ratePerUnit : defaults.ratePerUnit),
        effectiveMonth: current?.effectiveMonth || firstOpenMonth(settings?.lockedThrough, now),
        openingStockDate: current?.openingStockDate || '',
        note: '',
    };
};

/**
 * Whether the form differs from what was loaded, so there is something to save. The note alone
 * is not a change: it explains a change, and saving it by itself would add a history row with
 * the same settings.
 */
export const formChanged = (form, baseline) => {
    if (!form || !baseline) return false;
    const num = (v) => (String(v).trim() === '' ? NaN : Number(v));
    const sameNumber = (a, b) => num(a) === num(b) || (Number.isNaN(num(a)) && Number.isNaN(num(b)) && a === b);
    return form.enabled !== baseline.enabled
        || !sameNumber(form.freeMonths, baseline.freeMonths)
        || !sameNumber(form.ratePerUnit, baseline.ratePerUnit)
        || form.effectiveMonth !== baseline.effectiveMonth
        || (form.openingStockDate || '') !== (baseline.openingStockDate || '');
};

/** Problems with the form a person can fix before saving, or an empty list. */
export const formProblems = (form, lockedThrough, now = new Date(), lockedStatus = null) => {
    const problems = [];
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(form.effectiveMonth || '')) {
        problems.push('Choose the invoice month these settings take effect from (YYYY-MM).');
    } else if (lockedThrough && form.effectiveMonth <= lockedThrough) {
        // The server sends the status word with the month; "billed" only if it did not.
        problems.push(`The ${lockedThrough} invoice is already ${lockedStatus || 'billed'}; choose ${nextMonth(lockedThrough)} or later.`);
    }
    const blank = (v) => String(v ?? '').trim() === '';
    const free = Number(form.freeMonths);
    if (blank(form.freeMonths) || !Number.isInteger(free) || free < 0 || free > 60) {
        problems.push('The free storage period must be a whole number of months, from 0 to 60.');
    }
    const rate = Number(form.ratePerUnit);
    if (blank(form.ratePerUnit) || !Number.isFinite(rate) || rate < 0 || rate > 100) {
        problems.push('The rate per unit per month must be an amount in USD, from 0 to 100.');
    } else if (Math.abs(rate * 10000 - Math.round(rate * 10000)) > 1e-6) {
        problems.push('The rate per unit per month can have at most four decimal places.');
    }
    if (form.openingStockDate) {
        const date = new Date(`${form.openingStockDate}T00:00:00Z`);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(form.openingStockDate) || Number.isNaN(date.getTime())
            || date.toISOString().slice(0, 10) !== form.openingStockDate) {
            problems.push('The date stock already held counts from must be a real date (YYYY-MM-DD).');
        } else if (date.getTime() > now.getTime()) {
            problems.push('The date stock already held counts from cannot be in the future.');
        }
    }
    return problems;
};

/** "Nov 5, 2026, 3:00 AM" in the viewer's time, or a dash. */
export const formatWhen = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

/** One sentence about the latest stock reading, or why there is none yet. */
export const describeLastReading = (stock) => {
    const last = stock?.readings?.[0];
    if (!stock?.tracked) return 'Stock is read every night once storage fees are on.';
    if (!last) return 'Stock has not been read yet. It is read every night, or now with Read stock now.';
    if (last.status === 'OK') {
        const moved = last.opening
            ? 'first reading: everything on hand dated as stock already held'
            : `${last.unitsIn} in, ${last.unitsOut} out since the reading before`;
        // A ticked location deleted in Shopify is read as empty; the reading's note names it.
        const at = String(last.note || '').indexOf('Not read, deleted in Shopify');
        const deleted = at >= 0 ? ` ${last.note.slice(at)}` : '';
        return `Last read ${formatWhen(last.readAt)}: ${last.unitsOnHand} units on hand across ${last.items} items (${moved}).${deleted}`;
    }
    return `Last reading ${formatWhen(last.readAt)} ${last.status === 'SKIPPED' ? 'was skipped' : 'failed'}: ${last.note || 'no reason recorded'}`;
};

/** " - 40 past the 6-month free period today", or nothing when the brand has no setting. */
export const chargeableSentence = (stock) => {
    if (stock?.unitsChargeable === null || stock?.unitsChargeable === undefined) return '';
    if (Number(stock.freeMonths) === 0) return ` — all ${stock.unitsChargeable} charged (no free period)`;
    return ` — ${stock.unitsChargeable} past the ${stock.freeMonths}-month free period today`;
};

/** "1,234" */
const units = (n) => Number(n || 0).toLocaleString('en-US');

/** The message after an override is saved or undone, saying which it was. */
export const overrideMessage = (response, { clear = false } = {}) => {
    const c = response?.count || {};
    const month = monthLabel(c.month);
    if (response?.unchanged) return `${month} already charges ${units(c.unitsCharged)} units; nothing was changed.`;
    const fee = `$${Number(c.fee || 0).toFixed(2)}`;
    const rebuilt = response?.draftsUpdated ? ' The draft invoice was rebuilt.' : '';
    if (clear) return `Override removed: ${month} charges its counted ${units(c.unitsCharged)} units again, ${fee}.${rebuilt}`;
    if (c.enteredByHand) return `Entered by hand: ${month} charges ${units(c.unitsCharged)} units, ${fee}.${rebuilt}`;
    return `Overridden: ${month} now charges ${units(c.unitsCharged)} units (counted ${units(c.unitsCounted)}), ${fee}.${rebuilt}`;
};

/** "2,000 overridden (counted 2,435)" for an overridden count, "2,435 (entered by hand)" with no count. */
export const unitsChargedLabel = (count) => {
    if (count.overrideUnits === null || count.overrideUnits === undefined) return units(count.unitsCounted);
    if (count.enteredByHand) return `${units(count.overrideUnits)} (entered by hand)`;
    return `${units(count.overrideUnits)} overridden (counted ${units(count.unitsCounted)})`;
};

/** "000000001005 · Draft", or that no invoice is made yet. */
export const invoiceLabel = (count) => {
    if (!count.invoice) return 'Not made yet';
    const status = String(count.invoice.status || '');
    return `${count.invoice.invoiceNumber} · ${status.charAt(0)}${status.slice(1).toLowerCase()}`;
};

/** Problems with an override a person can fix before saving, or an empty list. */
export const overrideProblems = (form) => {
    const problems = [];
    const digits = String(form.units ?? '').trim();
    if (!/^\d+$/.test(digits) || Number(digits) > 10000000) problems.push('The units to charge must be a whole number, 0 or more.');
    if (!String(form.note || '').trim()) problems.push('Say why the units are being changed.');
    return problems;
};

/** The body PUT .../counts/:month/override takes: the units, or null to go back to the count. */
export const overrideBody = (form, { clear = false } = {}) => ({
    units: clear ? null : Number(String(form.units).trim()),
    note: String(form.note || '').trim(),
});

/** The body PUT /brands/:id/storage-fees takes. */
export const bodyFrom = (form) => ({
    enabled: form.enabled,
    effectiveMonth: form.effectiveMonth,
    freeMonths: Number(form.freeMonths),
    ratePerUnit: Number(form.ratePerUnit),
    openingStockDate: form.openingStockDate || null,
    note: form.note || null,
});

/** The heading badge: on, off, or on but not charged while shipping is disabled. */
export const storageBadgeLabel = (settings, shippingEnabled = true) => {
    if (!settings?.current?.enabled) return 'Off';
    return shippingEnabled ? 'On' : 'Not charged';
};
const storageBadge = (settings, shippingEnabled) => {
    const label = storageBadgeLabel(settings, shippingEnabled);
    return <Badge tone={label === 'On' ? 'success' : label === 'Not charged' ? 'warning' : undefined}>{label}</Badge>;
};

/** The card's heading line, read while the section is closed. */
export const storageSummary = (settings, shippingEnabled = true) => (shippingEnabled
    ? describeStorage(settings)
    : 'Not counted or charged while shipping is disabled for this brand.');

/**
 * Whether the closed card should open by itself: storage is being charged and cannot be counted
 * - no 2W location ticked, or the last stock reading failed.
 */
export const storageNeedsAttention = ({ settings, shippingEnabled = true, noLocations = false, stock = null }) => {
    if (!shippingEnabled || !settings?.current?.enabled) return false;
    return noLocations || stock?.readings?.[0]?.status === 'FAILED';
};

/** What the card says while the brand's shipping is disabled. */
export const SHIPPING_OFF_NOTICE = 'Shipping is disabled for this brand, so storage is not counted or charged, and '
    + 'invoices not yet approved show no storage fee. Enable shipping to charge storage again; these settings are kept.';

export const StorageFeeSettings = ({
    brand, shippingEnabled = true, saving, setSaving, setError, disabled: disabledProp = false,
}) => {
    // Shipping off locks the whole card: nothing about storage can be changed or charged.
    const disabled = disabledProp || !shippingEnabled;
    const [settings, setSettings] = useState(null);
    const [stock, setStock] = useState(null);
    const [reading, setReading] = useState(false);
    const [readNotice, setReadNotice] = useState(null);
    const [form, setForm] = useState(null);
    const [baseline, setBaseline] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [saved, setSaved] = useState(null);
    const [counts, setCounts] = useState(null);
    const [countNotice, setCountNotice] = useState(null);
    const [rerunning, setRerunning] = useState(false);
    const [override, setOverride] = useState(null);
    const [overriding, setOverriding] = useState(false);

    const load = useCallback(async () => {
        if (!brand?.id) return;
        setLoading(true);
        try {
            const [response, stockResponse, countsResponse] = await Promise.all([
                apiClient.getStorageFees(brand.id),
                apiClient.getStorageStock(brand.id).catch(() => null),
                apiClient.getStorageCounts(brand.id).catch(() => null),
            ]);
            setSettings(response);
            setForm(formFrom(response));
            setBaseline(formFrom(response));
            setStock(stockResponse);
            setCounts(countsResponse);
            setLoadFailed(false);
        } catch (err) {
            setLoadFailed(true);
        } finally {
            setLoading(false);
        }
    }, [brand?.id]);

    useEffect(() => { load(); }, [load, shippingEnabled]);

    // Only for a form someone has changed: the settings in force can start in a month that has
    // since been billed, which is not a problem until a change would be saved from it.
    const problems = useMemo(
        () => (form && formChanged(form, baseline) ? formProblems(form, settings?.lockedThrough, new Date(), settings?.lockedStatus) : []),
        [form, baseline, settings],
    );
    const noLocations = (brand?.twShippingLocationIds || []).length === 0;
    const changed = formChanged(form, baseline);
    // Closed until opened, unless something stops storage being counted.
    const [open, toggleOpen] = useSectionOpen(storageNeedsAttention({ settings, shippingEnabled, noLocations, stock }));

    const save = useCallback(async () => {
        if (!form || problems.length > 0 || !formChanged(form, baseline)) return;
        setSaving(true);
        setSaved(null);
        try {
            const response = await apiClient.saveStorageFees(brand.id, bodyFrom(form));
            setSettings(response);
            setForm(formFrom(response));
            setBaseline(formFrom(response));
            const stale = (response.recountNeeded || []).map(monthLabel);
            setSaved(savedMessage(response)
                + (stale.length
                    ? ` ${stale.join(', ')} ${stale.length === 1 ? 'was' : 'were'} already counted with the old free period or rate;`
                    + ' count again or override the units to charge under the new settings.'
                    : ''));
            setCounts(await apiClient.getStorageCounts(brand.id).catch(() => null));
        } catch (err) {
            setError(err.message || 'Saving the storage fee settings failed — nothing was changed.');
        } finally {
            setSaving(false);
        }
    }, [form, baseline, problems, brand?.id, setSaving, setError]);

    const readNow = useCallback(async () => {
        setReading(true);
        setReadNotice(null);
        try {
            await apiClient.readStorageStock(brand.id);
            setReadNotice('Stock reading queued. It usually finishes within a minute — refresh the stock to see it.');
        } catch (err) {
            setError(err.message || 'The stock reading could not be queued.');
        } finally {
            setReading(false);
        }
    }, [brand?.id, setError]);

    const refreshStock = useCallback(async () => {
        try {
            setStock(await apiClient.getStorageStock(brand.id));
            setReadNotice(null);
        } catch (err) {
            setError(err.message || 'The stock could not be loaded.');
        }
    }, [brand?.id, setError]);

    const refreshCounts = useCallback(async () => {
        try {
            setCounts(await apiClient.getStorageCounts(brand.id));
        } catch (err) {
            setError(err.message || 'The storage counts could not be loaded.');
        }
    }, [brand?.id, setError]);

    const rerun = useCallback(async () => {
        const month = counts?.rerun?.month;
        if (!month) return;
        setRerunning(true);
        setCountNotice(null);
        try {
            await apiClient.rerunStorageCount(brand.id, month);
            setCountNotice(`Counting ${monthLabel(month)} again. It usually finishes within a minute — refresh the counts to see it.`);
        } catch (err) {
            setError(err.message || 'The count could not be queued.');
        } finally {
            setRerunning(false);
        }
    }, [brand?.id, counts, setError]);

    const saveOverride = useCallback(async ({ clear = false } = {}) => {
        if (!override) return;
        setOverriding(true);
        setCountNotice(null);
        try {
            const response = await apiClient.overrideStorageCount(brand.id, override.month, overrideBody(override, { clear }));
            const c = response.count;
            setCountNotice(overrideMessage(response, { clear }));
            setOverride(null);
            await refreshCounts();
        } catch (err) {
            setError(err.message || 'The override was not saved — nothing was changed.');
        } finally {
            setOverriding(false);
        }
    }, [brand?.id, override, refreshCounts, setError]);

    if (loading) {
        return <InlineStack gap="200" blockAlign="center"><Spinner size="small" accessibilityLabel="Loading storage fees" /><Text tone="subdued">Loading storage fees…</Text></InlineStack>;
    }
    if (loadFailed || !form) {
        return (
            <Banner tone="critical" title="Storage fee settings could not be loaded">
                <InlineStack gap="200"><Button onClick={load}>Try again</Button></InlineStack>
            </Banner>
        );
    }

    const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));
    const history = settings?.history || [];

    return (
        <BlockStack gap="400">
            <BlockStack gap="100">
                <InlineStack align="space-between" blockAlign="center" gap="200">
                    <InlineStack gap="200" blockAlign="center">
                        <Text variant="headingMd" as="h3">Storage Fees</Text>
                        {storageBadge(settings, shippingEnabled)}
                    </InlineStack>
                    <InlineStack gap="200" blockAlign="center">
                        {/* Enabled only once a setting differs from what was loaded. */}
                        {open && (
                            <Button variant="primary" onClick={save} loading={saving} disabled={disabled || !changed || problems.length > 0}>
                                Save storage fees
                            </Button>
                        )}
                        <SectionToggle open={open} onToggle={toggleOpen} controls={`storage-fees-body-${brand?.id}`} />
                    </InlineStack>
                </InlineStack>
                <Text tone="subdued">{storageSummary(settings, shippingEnabled)}</Text>
            </BlockStack>

            <SectionBody open={open} id={`storage-fees-body-${brand?.id}`}>
            <BlockStack gap="400">
                <Text variant="bodySm" tone="subdued">
                    Counted on the 1st of each month at the 2W shipping locations ticked above, and charged on the
                    invoice of the month just ended. A unit is charged once it has been there longer than the free
                    storage period.
                </Text>

            {!shippingEnabled && <Banner tone="warning">{SHIPPING_OFF_NOTICE}</Banner>}

            {noLocations && shippingEnabled && (
                <Banner tone="warning">
                    {'No 2W shipping locations are ticked above, so there is no stock to count. Tick 2W\'s warehouse '
                        + 'location(s) in Shipping Configuration before turning storage fees on.'}
                </Banner>
            )}

            {saved && <Banner tone="success" onDismiss={() => setSaved(null)}>{saved}</Banner>}

            <BlockStack gap="300">
                <Checkbox
                    label="Charge this brand for storage"
                    checked={form.enabled}
                    onChange={set('enabled')}
                    disabled={disabled || saving}
                />
                <InlineGrid columns={{ xs: 1, md: 3 }} gap="300">
                    <TextField
                        label="Free storage period (months)"
                        type="number"
                        min={0}
                        value={form.freeMonths}
                        onChange={set('freeMonths')}
                        helpText="How long a unit sits at 2W before it is charged. 0 charges every unit from day one."
                        autoComplete="off"
                        disabled={disabled || saving || !form.enabled}
                    />
                    <TextField
                        label="Rate per unit per month (USD)"
                        type="number"
                        min={0}
                        step={0.01}
                        prefix="$"
                        value={form.ratePerUnit}
                        onChange={set('ratePerUnit')}
                        autoComplete="off"
                        disabled={disabled || saving || !form.enabled}
                    />
                    <TextField
                        label="Takes effect from invoice month"
                        type="month"
                        value={form.effectiveMonth}
                        onChange={set('effectiveMonth')}
                        helpText={effectiveMonthHelp(settings)}
                        autoComplete="off"
                        disabled={disabled || saving}
                    />
                </InlineGrid>
                <InlineGrid columns={{ xs: 1, md: 2 }} gap="300">
                    <TextField
                        label="Stock already held counts from"
                        type="date"
                        value={form.openingStockDate}
                        onChange={set('openingStockDate')}
                        helpText={(stock?.readings || []).some((r) => r.status === 'OK')
                            ? 'Used on the first stock reading only. Stock has already been read, so changing this now changes nothing.'
                            : 'The arrival date given to stock already at 2W when tracking starts, which Shopify cannot tell us. Leave empty for the day tracking starts, so existing stock is free until the free period has passed.'}
                        autoComplete="off"
                        disabled={disabled || saving || !form.enabled}
                    />
                    <TextField
                        label="Note"
                        value={form.note}
                        onChange={set('note')}
                        helpText="Why it changed, for the next person. Kept in the history and the Activity Log."
                        autoComplete="off"
                        disabled={disabled || saving}
                    />
                </InlineGrid>
                {problems.length > 0 && (
                    <BlockStack gap="050">
                        {problems.map((p) => <Text key={p} tone="critical" variant="bodySm">{p}</Text>)}
                    </BlockStack>
                )}
            </BlockStack>

            <BlockStack gap="200">
                <InlineStack align="space-between" blockAlign="center">
                    <Text variant="headingSm" as="h4">Stock at 2W locations</Text>
                    <InlineStack gap="200">
                        <Button onClick={refreshStock} disabled={reading}>Refresh</Button>
                        <Button onClick={readNow} loading={reading} disabled={disabled || noLocations || !stock?.tracked}>
                            Read stock now
                        </Button>
                    </InlineStack>
                </InlineStack>
                <Text tone="subdued">{describeLastReading(stock)}</Text>
                {readNotice && <Banner tone="info" onDismiss={() => setReadNotice(null)}>{readNotice}</Banner>}
                {stock && stock.unitsOnHand > 0 && (
                    <BlockStack gap="200">
                        <Text>
                            {`${stock.unitsOnHand} units on hand`}
                            {chargeableSentence(stock)}
                            {stock.oldestReceivedOn ? `; oldest since ${formatWhen(stock.oldestReceivedOn)}.` : '.'}
                        </Text>
                        <DataTable
                            columnContentTypes={['text', 'numeric']}
                            headings={['Held for', 'Units']}
                            rows={(stock.bands || []).map((b) => [b.label, String(b.units)])}
                        />
                    </BlockStack>
                )}
            </BlockStack>

            <BlockStack gap="200">
                <InlineStack align="space-between" blockAlign="center">
                    <Text variant="headingSm" as="h4">Monthly counts</Text>
                    <InlineStack gap="200">
                        <Button onClick={refreshCounts} disabled={rerunning}>Refresh</Button>
                        {counts?.rerun && (
                            <Button onClick={rerun} loading={rerunning} disabled={disabled || !counts.rerun.allowed}>
                                {counts.rerun.counted === false
                                    ? `Count ${monthLabel(counts.rerun.month)} now`
                                    : `Count ${monthLabel(counts.rerun.month)} again`}
                            </Button>
                        )}
                        {counts?.rerun?.allowed && counts.rerun.counted === false && (
                            <Button
                                disabled={disabled || overriding}
                                onClick={() => setOverride({
                                    month: counts.rerun.month, units: '', note: '', overridden: false, unitsCounted: null,
                                })}
                            >
                                {`Enter ${monthLabel(counts.rerun.month)} units by hand`}
                            </Button>
                        )}
                    </InlineStack>
                </InlineStack>
                <Text variant="bodySm" tone="subdued">
                    {counts?.rerun && !counts.rerun.allowed
                        ? `${monthLabel(counts.rerun.month)} cannot be counted again: ${counts.rerun.reason}`
                        : 'Counting again reads stock as it is now and replaces any override. An earlier month is corrected by overriding its units.'}
                </Text>
                {countNotice && <Banner tone="info" onDismiss={() => setCountNotice(null)}>{countNotice}</Banner>}
                <DataTable
                    columnContentTypes={['text', 'text', 'numeric', 'text', 'numeric', 'numeric', 'text', 'text']}
                    headings={['Invoice month', 'Counted', 'On hand', 'Units charged', 'Rate', 'Fee', 'Invoice', '']}
                    rows={(counts?.counts || []).length > 0
                        ? counts.counts.map((c) => [
                            monthLabel(c.month),
                            formatWhen(c.countedAt),
                            units(c.unitsOnHand),
                            c.overrideNote ? `${unitsChargedLabel(c)} — ${c.overrideNote}` : unitsChargedLabel(c),
                            formatRate(c.ratePerUnit),
                            `$${Number(c.fee).toFixed(2)}`,
                            invoiceLabel(c),
                            c.canChange
                                ? (
                                    <Button
                                        key={c.month}
                                        size="slim"
                                        disabled={disabled || overriding}
                                        onClick={() => setOverride({
                                            month: c.month,
                                            units: String(c.unitsCharged),
                                            note: '',
                                            overridden: c.overrideUnits !== null && c.overrideUnits !== undefined,
                                            unitsCounted: c.unitsCounted,
                                        })}
                                    >
                                        {c.overrideUnits !== null && c.overrideUnits !== undefined
                                            ? 'Change or undo override'
                                            : 'Override units'}
                                    </Button>
                                )
                                : 'Billed',
                        ])
                        : [['No month counted yet.', '', '', '', '', '', '', '']]}
                />
                {override && (
                    <BlockStack gap="200">
                        <Text variant="headingSm" as="h5">
                            {override.unitsCounted === null
                                ? `${monthLabel(override.month)} was not counted — enter the units to charge`
                                : `Override ${monthLabel(override.month)} — counted ${units(override.unitsCounted)} units`}
                        </Text>
                        {override.overridden && (
                            <Text variant="bodySm" tone="subdued">
                                To undo the override, write why below and press Go back to the count. The month is then charged its counted units again.
                            </Text>
                        )}
                        <InlineGrid columns={{ xs: 1, md: 2 }} gap="300">
                            <TextField
                                label="Units to charge"
                                type="number"
                                min={0}
                                value={override.units}
                                onChange={(v) => setOverride((prev) => ({ ...prev, units: v }))}
                                autoComplete="off"
                                disabled={overriding}
                            />
                            <TextField
                                label="Why"
                                value={override.note}
                                onChange={(v) => setOverride((prev) => ({ ...prev, note: v }))}
                                helpText="Required. Shown with the count, on the invoice's update note and in the Activity Log."
                                autoComplete="off"
                                disabled={overriding}
                            />
                        </InlineGrid>
                        {overrideProblems(override).map((p) => <Text key={p} tone="critical" variant="bodySm">{p}</Text>)}
                        <InlineStack align="end" gap="200">
                            <Button onClick={() => setOverride(null)} disabled={overriding}>Cancel</Button>
                            {override.overridden && (
                                <Button
                                    onClick={() => saveOverride({ clear: true })}
                                    disabled={overriding || !String(override.note).trim()}
                                >
                                    Go back to the count
                                </Button>
                            )}
                            <Button
                                variant="primary"
                                onClick={() => saveOverride()}
                                loading={overriding}
                                disabled={overrideProblems(override).length > 0}
                            >
                                Save override
                            </Button>
                        </InlineStack>
                    </BlockStack>
                )}
            </BlockStack>

            <BlockStack gap="200">
                <Text variant="headingSm" as="h4">History</Text>
                <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
                    headings={['From invoice', 'Storage', 'Free period', 'Rate per unit / month', 'Note', 'Set by']}
                    rows={history.length > 0
                        ? history.map((h) => [
                            monthLabel(h.effectiveMonth),
                            h.enabled ? 'On' : 'Off',
                            h.enabled ? formatFreeMonths(h.freeMonths) : '—',
                            h.enabled ? formatRate(h.ratePerUnit) : '—',
                            h.note || '—',
                            h.decidedBy || '—',
                        ])
                        : [['No storage settings saved yet.', '', '', '', '', '']]}
                />
            </BlockStack>
            </BlockStack>
            </SectionBody>
        </BlockStack>
    );
};

export default StorageFeeSettings;
